import React from "react";
import PropTypes from "prop-types";
import Popper from "@material-ui/core/Popper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { Transition } from "react-transition-group";
import FocusTrap from "react-focus-trap";
import useOpenClose from "../../hooks/use-open-close";
import uuid from "uuid/v4";
import classNames from "classnames";
import PlainButton from "../elements/plain-button";

// Commenting out b/c nested popovers is bad UX but if ever needed:
// let popper_stack = []; // track a global
// manages the popper_stack
// React.useEffect(() => {
//   const popper_id = uid.current;
//   let stack_idx;
//   if (is_open) {
//     const idx = popper_stack.indexOf(popper_id);
//     if (idx === -1) {
//       stack_idx = popper_stack.length;
//       popper_stack.push(popper_id); // add new, open popper id to the stack
//     }
//   }

//   return () => {
//     if (stack_idx > -1) {
//       popper_stack = popper_stack.filter(id => id !== popper_id);
//     }
//   };
// }, [is_open]);

// this would be called before an outsideClick close or an escape key
// const isTop = id => {
//   const idx = popper_stack.indexOf(id);
//   if (idx === popper_stack.length - 1) return true;
//   return false;
// };

const Popover2 = ({
  borderColor,
  wrapperClassName,
  contentWidth,
  trigger,
  children,
  disablePortal,
  showArrow = false,
  placement,
  duration = 100,
  transitionProps,
  disableAnimation,
  contentClassName,
  disabled,
  onClose = () => {},
  ...rest
}) => {
  const [is_open, open, close] = useOpenClose();
  const [arrow_ref, setArrow] = React.useState(null);
  const anchorRef = React.useRef(null);
  const uid = React.useRef(uuid());
  const id = is_open ? uid.current : null;

  const handleOpen = () => {
    if (!is_open && !disabled) {
      open();
    }
  };

  const handleClose = () => {
    if (is_open && !disabled) {
      close();
      onClose();
    }
  };

  React.useEffect(() => {
    const closeOnEsc = e => {
      if (is_open && e.key === "Escape") {
        handleClose();
      }
    };
    document.body.addEventListener("keydown", closeOnEsc, true);
    return () => {
      document.body.removeEventListener("keydown", closeOnEsc);
    };
  }, [is_open, close]);

  const onOutsideClick = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    handleClose();
  };
  const triggerEl =
    typeof trigger === "function" ? (
      React.cloneElement(
        trigger({
          is_open,
          openPopover: handleOpen,
          closePopover: handleClose,
          togglePopover: is_open ? handleClose : handleOpen,
        }),
        { ref: anchorRef, "aria-describedby": id }
      )
    ) : (
      <PlainButton ref={anchorRef} aria-describedby={id} onClick={is_open ? handleClose : handleOpen}>
        {trigger}
      </PlainButton>
    );

  return (
    <>
      {triggerEl}
      <Popper
        className="et--popover2--outer"
        modifiers={{
          ...(showArrow && {
            arrow: {
              enabled: true,
              element: arrow_ref,
            },
          }),
        }}
        id={id}
        open={is_open}
        anchorEl={anchorRef.current}
        disablePortal={disablePortal}
        placement={placement}
        transition
        {...rest}
      >
        {({ placement, TransitionProps }) => (
          <>
            {showArrow ? <span className={"et--popover2--arrow " + placement} ref={e => setArrow(e)} /> : null}
            <ClickAwayListener onClickAway={onOutsideClick}>
              <div className={wrapperClassName}>
                <Transition
                  in={TransitionProps.in}
                  timeout={duration}
                  appear={true}
                  unmountOnExit
                  mountOnEnter
                  {...transitionProps}
                >
                  {state => (
                    <FocusTrap>
                      <div
                        className={classNames({ "et--popover2": !disableAnimation }, contentClassName, state)}
                        style={{ width: contentWidth }}
                      >
                        {typeof children === "function" ? children({ closePopover: handleClose }) : children}
                      </div>
                    </FocusTrap>
                  )}
                </Transition>
              </div>
            </ClickAwayListener>
          </>
        )}
      </Popper>
    </>
  );
};

Popover2.propTypes = {
  disabled: PropTypes.bool,
  borderColor: PropTypes.string,
  children: PropTypes.any,
  contentClassName: PropTypes.string,
  contentWidth: PropTypes.number,
  disableAnimation: PropTypes.bool,
  disablePortal: PropTypes.bool,
  duration: PropTypes.number,
  placement: PropTypes.string,
  showArrow: PropTypes.bool,
  transitionProps: PropTypes.object,
  trigger: PropTypes.any,
  wrapperClassName: PropTypes.any,
  onClose: PropTypes.func,
};

export default Popover2;
